import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import API_BASE_URL from '../config';
import './ShadiWala.css';

interface ShadiWalaRecs {
    halls: any[];
    foodMenu: {
        season: string;
        dietary: string;
        note: string;
        categories: { category: string, items: string[] }[];
    };
    vendors: any[];
    clothing: { role: string, items: { name: string, fabric: string, color: string, reason: string, personalNote: string }[] }[];
}

const ShadiWala: React.FC = () => {
    const { accessToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [recs, setRecs] = useState<ShadiWalaRecs | null>(null);

    // Form states
    const [location, setLocation] = useState('Mumbai');
    const [budget, setBudget] = useState('1000000');
    const [month, setMonth] = useState('12');
    const [expectedGuests, setExpectedGuests] = useState('200');
    const [dietary, setDietary] = useState('veg');
    const [homeCity, setHomeCity] = useState('');
    
    const [brideFairness, setBrideFairness] = useState('fair');
    const [brideHeight, setBrideHeight] = useState('160');
    const [brideWeight, setBrideWeight] = useState('55');
    
    const [groomFairness, setGroomFairness] = useState('fair');
    const [groomHeight, setGroomHeight] = useState('175');
    const [groomWeight, setGroomWeight] = useState('70');

    const handleGetRecs = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/shadi-wala/recommend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    location,
                    budget,
                    month,
                    expectedGuests,
                    dietaryPreference: dietary,
                    homeCity,
                    brideFairness,
                    brideHeight,
                    brideWeight,
                    groomFairness,
                    groomHeight,
                    groomWeight
                }),
            });
            const data = await res.json();
            if (res.ok) {
                setRecs(data.recommendations);
            }
        } catch (err) {
            console.error('Error fetching recs:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="shadi-wala-page">
            
            <div className="shadi-wala-container">
                <header className="shadi-wala-header">
                    <div className="ai-badge">AI AGENT</div>
                    <h1>Shadi Wala</h1>
                    <p>Your Personal AI Wedding Consultant</p>
                </header>

                <div className="shadi-wala-content">
                    <section className="form-section glass-card">
                        <div className="section-header">
                            <span className="section-icon">🧠</span>
                            <h2>Intelligent Inputs</h2>
                        </div>
                        <form onSubmit={handleGetRecs} className="recommendation-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Wedding Location (City)</label>
                                    <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Mumbai" required />
                                </div>
                                <div className="form-group">
                                    <label>Total Budget (₹)</label>
                                    <input type="number" value={budget} onChange={e => setBudget(e.target.value)} required />
                                </div>
                                <div className="form-group">
                                    <label>Wedding Month</label>
                                    <select value={month} onChange={e => setMonth(e.target.value)}>
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Expected Guests</label>
                                    <input type="number" value={expectedGuests} onChange={e => setExpectedGuests(e.target.value)} required />
                                </div>
                                <div className="form-group">
                                    <label>Dietary Habits</label>
                                    <select value={dietary} onChange={e => setDietary(e.target.value)}>
                                        <option value="veg">Pure Veg</option>
                                        <option value="non-veg">Non-Veg Included</option>
                                        <option value="both">Both</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Your Home City (for Distance)</label>
                                    <input type="text" value={homeCity} onChange={e => setHomeCity(e.target.value)} placeholder="e.g. Pune" />
                                </div>
                            </div>

                            <div className="personal-details-grid">
                                <div className="person-box">
                                    <h3>👰 Bride Details</h3>
                                    <div className="inner-grid">
                                        <div className="form-group">
                                            <label>Complexion</label>
                                            <select value={brideFairness} onChange={e => setBrideFairness(e.target.value)}>
                                                <option value="fair">Fair</option>
                                                <option value="medium">Medium</option>
                                                <option value="dark">Dark / Rich</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Height (cm)</label>
                                            <input type="number" value={brideHeight} onChange={e => setBrideHeight(e.target.value)} />
                                        </div>
                                        <div className="form-group">
                                            <label>Weight (kg)</label>
                                            <input type="number" value={brideWeight} onChange={e => setBrideWeight(e.target.value)} />
                                        </div>
                                    </div>
                                </div>

                                <div className="person-box">
                                    <h3>🤵 Groom Details</h3>
                                    <div className="inner-grid">
                                        <div className="form-group">
                                            <label>Complexion</label>
                                            <select value={groomFairness} onChange={e => setGroomFairness(e.target.value)}>
                                                <option value="fair">Fair</option>
                                                <option value="medium">Medium</option>
                                                <option value="dark">Dark / Rich</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Height (cm)</label>
                                            <input type="number" value={groomHeight} onChange={e => setGroomHeight(e.target.value)} />
                                        </div>
                                        <div className="form-group">
                                            <label>Weight (kg)</label>
                                            <input type="number" value={groomWeight} onChange={e => setGroomWeight(e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button type="submit" className="get-recs-btn" disabled={loading}>
                                {loading ? '🤖 Calculating Suggestions...' : '✨ Generate AI Recommendations'}
                            </button>
                        </form>
                    </section>

                    <AnimatePresence>
                        {recs && (
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }} 
                                animate={{ opacity: 1, y: 0 }}
                                className="recommendations-results"
                            >
                                {/* 1. Marriage Halls */}
                                <div className="result-section">
                                    <h3>🏰 Recommended Marriage Halls</h3>
                                    <div className="halls-grid">
                                        {recs.halls.map((hall, idx) => (
                                            <div key={idx} className="hall-card">
                                                <div className="hall-header">
                                                    <h4>{hall.name}</h4>
                                                    <span className={`suitability-tag ${hall.withinBudget ? 'good' : 'bad'}`}>
                                                        {hall.suitability}
                                                    </span>
                                                </div>
                                                <p className="hall-location">📍 {hall.city} • {hall.distance}</p>
                                                <div className="hall-stats">
                                                    <span>Capacity: <strong>{hall.capacity}</strong></span>
                                                    <span>Base: <strong>₹{hall.pricePerPlate}/plate</strong></span>
                                                </div>
                                                <div className="hall-features">
                                                    {hall.features.slice(0, 3).map((f: string, i: number) => (
                                                        <span key={i} className="feature-pill">{f}</span>
                                                    ))}
                                                </div>
                                                <div className="hall-footer">
                                                    <span className="estimated-price">Total Est: {hall.estimatedCost}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 2. Food Menu */}
                                <div className="result-section">
                                    <h3>🍱 Curated Food Menu ({recs.foodMenu.season} Season)</h3>
                                    <div className="menu-note">{recs.foodMenu.note}</div>
                                    <div className="menu-categories">
                                        {recs.foodMenu.categories.map((cat, idx) => (
                                            <div key={idx} className="menu-cat">
                                                <h4>{cat.category}</h4>
                                                <ul>
                                                    {cat.items.map((item, i) => <li key={i}>{item}</li>)}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 3. Food Vendors */}
                                <div className="result-section">
                                    <h3>🍲 Recommended Food Vendors</h3>
                                    <div className="vendors-list">
                                        {recs.vendors.map((vendor, idx) => (
                                            <div key={idx} className="vendor-item">
                                                <div className="vendor-info">
                                                    <h4>{vendor.name}</h4>
                                                    <p>Specialize in: {vendor.specialty}</p>
                                                </div>
                                                <div className="vendor-meta">
                                                    <span className="vendor-price">{vendor.priceRange}</span>
                                                    <span className="vendor-rating">⭐ {vendor.rating}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 4. Clothing */}
                                <div className="result-section">
                                    <h3>👗 High-End Clothing Recommendations</h3>
                                    <div className="clothing-grid">
                                        {recs.clothing.map((role, idx) => (
                                            <div key={idx} className="clothing-box">
                                                <h4>{role.role}</h4>
                                                <div className="clothing-items">
                                                    {role.items.map((item, i) => (
                                                        <div key={i} className="clothing-item-card">
                                                            <div className="item-main">
                                                                <span className="item-name">{item.name}</span>
                                                                <span className="item-fabric">{item.fabric}</span>
                                                            </div>
                                                            <div className="item-color">Colors: {item.color}</div>
                                                            <div className="item-reason">{item.reason}</div>
                                                            <div className="personal-note">💡 {item.personalNote}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default ShadiWala;
